/**
 * AddFirstChildModal component
 * Accessible modal for creating the family's first child profile in the
 * US-005 trial onboarding flow.
 *
 * Implements US-005 (T3): AddFirstChildModal.
 *
 * Distinct from AddChildDialog (US-004):
 *  - Pre-fills display name with "My Child" (AC-1 default, editable)
 *  - Has a confirm-password field (UX friction-reducer for first-time parents)
 *  - Shows friendly onboarding copy about the trial
 *  - On success: stays open and shows the child's login credentials (username)
 *    so the child can immediately log in (no placeholder — real API username shown)
 *  - On close/Done: resets all form state including passwords (security)
 *
 * Overlay pattern reused from AddChildDialog (DemoLimitPopup shell):
 *   role=dialog, aria-modal, Escape-to-close, focus management on open.
 *
 * Reused primitives: Input, Label, Button (ui/), validatePassword, PasswordRequirements.
 *
 * TO-LL-044 enforcement: submit payload uses exact camelCase keys displayName/password
 * matching children.py lines 126-128 (via CreateChildPayload type in useCreateChild).
 * TO-LL-036 enforcement: errors from the mutation are surfaced inline — modal stays open
 * on 400/409/500; never swallowed. Error path tested in US-005 smoke tests (T5).
 * TO-LL-013 enforcement: no hardcoded path navigation in this component; all navigation
 * is modal open/close only (no navigate() calls).
 *
 * AC-1: creates child profile with pre-filled "My Child" via POST /children.
 * AC-2: trial CTA (controlled by parent page) is only shown when children.length === 0,
 *       so this modal is the mechanism that prevents a second "default child".
 * AC-3: success view notes the child can be renamed later (rename contract verified).
 */

import { useEffect, useRef, useState, useCallback } from 'react';
import { Input } from '@/components/ui/input';
import { PasswordInput } from '@/components/ui/password-input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { PasswordRequirements } from '@/components/PasswordRequirements';
import { validatePassword } from '@/lib/passwordValidator';
import { useCreateChild } from '@/hooks/useCreateChild';
import {
  GRADE_LEVEL_OPTIONS,
  P6_BAND_OPTIONS,
  FIRST_CHILD_DEFAULT_DIFFICULTY_TIER,
} from '@/constants/difficulty';
import type { DifficultyTier } from '@/types/coins';

// ─── Props ────────────────────────────────────────────────────────────────────

export interface AddFirstChildModalProps {
  /** Whether the modal is visible */
  isOpen: boolean;
  /** Callback to close the modal — called after Done in success view or Cancel/Escape */
  onClose: () => void;
}

// ─── AddFirstChildModal (main export) ────────────────────────────────────────

/**
 * AddFirstChildModal — overlay modal for the US-005 first-child trial onboarding.
 *
 * Fields (idle state):
 *  - Child's name (pre-filled "My Child", editable — AC-1)
 *  - Username (required — parent chooses the child's login; no longer auto-generated)
 *  - Password (with live PasswordRequirements checklist)
 *  - Confirm password (matches check before API call)
 *
 * States:
 *  - Idle: form editable, default name "My Child"
 *  - Submitting (isPending): buttons and inputs disabled
 *  - Error (mutation.error or clientErrors): inline message, form stays open (TO-LL-036)
 *  - Success (isSuccess + data set): instructions panel with REAL returned username
 */
export function AddFirstChildModal({ isOpen, onClose }: AddFirstChildModalProps) {
  const { create, isPending, error, isSuccess, reset, data } = useCreateChild();

  // ─── Form state ───────────────────────────────────────────────────────────

  const [displayName, setDisplayName] = useState('My Child');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  // Learning level. Starts at the EASIEST P6 band rather than the global read-time default,
  // per Wayne 2026-07-27: "for P6, default difficulty to the easiest level".
  const [difficultyTier, setDifficultyTier] = useState<DifficultyTier>(
    FIRST_CHILD_DEFAULT_DIFFICULTY_TIER
  );
  const [clientErrors, setClientErrors] = useState<Record<string, string>>({});

  // Focus target: child name input (auto-focused on dialog open)
  const displayNameRef = useRef<HTMLInputElement>(null);

  // ─── Reset all state when modal opens ────────────────────────────────────

  useEffect(() => {
    if (isOpen) {
      setDisplayName('My Child');
      setUsername('');
      setPassword('');
      setConfirmPassword('');
      // Reset the level too, or a selection from a previous open silently carries over into
      // the next child — every other field here is reset for the same reason.
      setDifficultyTier(FIRST_CHILD_DEFAULT_DIFFICULTY_TIER);
      setClientErrors({});
      reset(); // Clear any stale mutation state from previous open
      // Focus the name field after DOM is ready
      const timer = setTimeout(() => {
        displayNameRef.current?.focus();
      }, 50);
      return () => clearTimeout(timer);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // ─── Clear passwords on close for security ────────────────────────────────

  const handleClose = useCallback(() => {
    // Clear password fields before dismissing (security: no lingering sensitive state)
    setPassword('');
    setConfirmPassword('');
    onClose();
  }, [onClose]);

  // ─── Keyboard handler: Escape dismisses the modal ────────────────────────

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Escape' && !isPending) {
        handleClose();
      }
    },
    [handleClose, isPending]
  );

  // ─── Client-side validation ───────────────────────────────────────────────

  /**
   * Validate form fields BEFORE calling the API.
   * Returns true if valid; false + sets clientErrors if any rule fails.
   *
   * Rules (Cognito policy from CLAUDE.md + UX):
   *   - displayName: trimmed non-empty
   *   - password: validatePassword() must pass (min 8, upper, lower, number)
   *   - confirmPassword: must match password exactly
   */
  const validateForm = useCallback((): boolean => {
    const errors: Record<string, string> = {};

    const trimmedName = displayName.trim();
    if (!trimmedName) {
      errors.displayName = 'Please enter a name for your child.';
    }

    // Username is required — the parent chooses the child's login (no auto-generate).
    if (!username.trim()) {
      errors.username = 'Please enter a username for your child.';
    }

    const { isValid: pwValid } = validatePassword(password);
    if (!pwValid) {
      errors.password = 'Password needs at least 8 characters with an uppercase letter, a lowercase letter and a number.';
    }

    if (password !== confirmPassword) {
      errors.confirmPassword = 'Passwords do not match. Please check and try again.';
    }

    setClientErrors(errors);
    return Object.keys(errors).length === 0;
  }, [displayName, username, password, confirmPassword]);

  // ─── Form submission ──────────────────────────────────────────────────────

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      // TO-LL-036: block submit and show errors instead of swallowing invalid state
      if (!validateForm()) return;

      // TO-LL-044: payload keys are exact camelCase matching children.py lines 126-128
      // TO-LL-036: no try/catch here — errors propagate to mutation.error
      create({
        displayName: displayName.trim(),
        password,
        // Username is now parent-chosen (required) — no longer auto-generated.
        username: username.trim(),
        difficultyTier,
      });
    },
    [validateForm, create, displayName, username, password, difficultyTier]
  );

  // Don't render when not open — zero modal render cost on initial dashboard load
  if (!isOpen) return null;

  // Determine inline error message: server error (TO-LL-036) or client validation
  // Map server error codes to neutral, non-leaking user copy (security consideration)
  const serverErrorMessage = (() => {
    if (!error) return null;
    const code = (error as { code?: string }).code ?? '';
    if (code === 'CONFLICT') {
      return "That login name is taken — leave the name blank to let us pick one for you.";
    }
    if (code === 'VALIDATION_ERROR') {
      return 'Password needs at least 8 characters with an uppercase letter, a lowercase letter and a number.';
    }
    return 'Something went wrong creating the profile. Please try again.';
  })();

  return (
    /* Overlay — same pattern as AddChildDialog: fixed inset-0 z-50, click backdrop to close */
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 overflow-y-auto py-4"
      role="presentation"
      onKeyDown={handleKeyDown}
      onClick={(e) => {
        // Click outside the dialog card closes it (only when not submitting and not in success)
        if (e.target === e.currentTarget && !isPending && !isSuccess) {
          handleClose();
        }
      }}
    >
      {/* Dialog card */}
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="add-first-child-modal-title"
        className="bg-white rounded-2xl shadow-lg p-6 md:p-8 w-full max-w-sm mx-4 relative"
        onClick={(e) => e.stopPropagation()} // Prevent backdrop-close from card clicks
      >
        {/* ── Success view (shown after 201 — real username from API) ────────── */}
        {isSuccess && data ? (
          <div className="text-center space-y-4">
            {/* Success icon */}
            <div className="mx-auto w-14 h-14 rounded-full bg-green-100 flex items-center justify-center">
              <svg
                className="w-8 h-8 text-green-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
                aria-hidden="true"
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            </div>

            <div>
              <h2
                id="add-first-child-modal-title"
                className="text-lg font-bold text-gray-900"
              >
                {data.displayName}'s account is ready!
              </h2>
              <p className="text-sm text-gray-500 mt-1">
                Share these details so your child can log in and start their first session.
              </p>
            </div>

            {/* Login credentials — real username from API response (AC-1, no placeholder) */}
            <div
              className="rounded-xl bg-tutor-50 border border-tutor-200 p-4 text-left space-y-2"
              aria-label="Child login details"
            >
              <div>
                <p className="text-xs font-semibold text-tutor-700 uppercase tracking-wide">
                  Login username
                </p>
                {/* TO-LL-044: data.username is the API-returned immutable Cognito username */}
                <p className="text-sm font-mono font-semibold text-gray-900 mt-0.5 break-all">
                  {data.username}
                </p>
              </div>
              <div>
                <p className="text-xs font-semibold text-tutor-700 uppercase tracking-wide">
                  Password
                </p>
                <p className="text-sm text-gray-600 mt-0.5">
                  The password you just created
                </p>
              </div>
            </div>

            <p className="text-xs text-gray-400">
              Your child can log in with this username and the password you set, then tap{' '}
              <strong>Start</strong> to begin their first session.
              <br />
              You can rename{' '}
              <strong>{data.displayName}</strong> later from the dashboard — their login and history are not affected.
            </p>

            {/* Done button — closes modal; dashboard query already invalidated by useCreateChild */}
            <Button
              type="button"
              onClick={handleClose}
              className="w-full h-11"
            >
              Done
            </Button>
          </div>
        ) : (
          /* ── Form view (idle / submitting / error state) ───────────────────── */
          <>
            <h2
              id="add-first-child-modal-title"
              className="text-lg md:text-xl font-bold text-gray-900 mb-1"
            >
              Create your child's account
            </h2>
            <p className="text-sm text-gray-500 mb-5">
              Create your child's profile, then they can log in and start their first guided session — your welcome coin is ready.
            </p>

            <form onSubmit={handleSubmit} noValidate className="space-y-4">
              {/* Child display name — pre-filled "My Child" (AC-1) */}
              <div className="space-y-1">
                <Label
                  htmlFor="first-child-display-name"
                  className="text-sm font-medium text-gray-700"
                >
                  Child's name <span aria-hidden="true" className="text-red-500">*</span>
                </Label>
                <Input
                  id="first-child-display-name"
                  ref={displayNameRef}
                  type="text"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  placeholder="e.g. My Child"
                  maxLength={50}
                  required
                  disabled={isPending}
                  autoComplete="off"
                  className="h-11"
                  aria-describedby={clientErrors.displayName ? 'first-child-name-error' : undefined}
                />
                {clientErrors.displayName && (
                  <p id="first-child-name-error" className="text-xs text-red-600" role="alert">
                    {clientErrors.displayName}
                  </p>
                )}
              </div>

              {/* Username field (required) — parent chooses the child's login */}
              <div className="space-y-1">
                <Label
                  htmlFor="first-child-username"
                  className="text-sm font-medium text-gray-700"
                >
                  Username <span aria-hidden="true" className="text-red-500">*</span> <span className="text-xs font-normal text-gray-500">(case-sensitive)</span>
                </Label>
                <Input
                  id="first-child-username"
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="Choose a username"
                  required
                  disabled={isPending}
                  autoComplete="off"
                  className="h-11"
                  aria-describedby={clientErrors.username ? 'first-child-username-error' : undefined}
                />
                {clientErrors.username && (
                  <p id="first-child-username-error" className="text-xs text-red-600" role="alert">
                    {clientErrors.username}
                  </p>
                )}
                <p className="text-xs text-gray-400">Your child uses this to log in.</p>
              </div>

              {/* Password field + live requirements */}
              <div className="space-y-1">
                <Label
                  htmlFor="first-child-password"
                  className="text-sm font-medium text-gray-700"
                >
                  Password <span aria-hidden="true" className="text-red-500">*</span>
                </Label>
                <PasswordInput
                  id="first-child-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Create a password"
                  required
                  disabled={isPending}
                  autoComplete="new-password"
                  className="h-11"
                  aria-describedby={
                    clientErrors.password
                      ? 'first-child-password-error'
                      : password.length > 0
                      ? 'first-child-password-requirements'
                      : undefined
                  }
                />
                {/* Live policy checklist — shown when user starts typing */}
                {password.length > 0 && (
                  <div id="first-child-password-requirements" className="pt-1">
                    <PasswordRequirements password={password} />
                  </div>
                )}
                {clientErrors.password && (
                  <p id="first-child-password-error" className="text-xs text-red-600" role="alert">
                    {clientErrors.password}
                  </p>
                )}
              </div>

              {/* Confirm password field */}
              <div className="space-y-1">
                <Label
                  htmlFor="first-child-confirm-password"
                  className="text-sm font-medium text-gray-700"
                >
                  Confirm password <span aria-hidden="true" className="text-red-500">*</span>
                </Label>
                <PasswordInput
                  id="first-child-confirm-password"
                  toggleLabel="confirm password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Repeat the password"
                  required
                  disabled={isPending}
                  autoComplete="new-password"
                  className="h-11"
                  aria-describedby={
                    clientErrors.confirmPassword ? 'first-child-confirm-error' : undefined
                  }
                />
                {clientErrors.confirmPassword && (
                  <p id="first-child-confirm-error" className="text-xs text-red-600" role="alert">
                    {clientErrors.confirmPassword}
                  </p>
                )}
              </div>

              {/*
                Learning level. Mirrors the AddChildDialog control and the child-card dropdown
                verbatim, from the SAME shared options, so the three surfaces cannot drift.

                P4 and P5 are single choices; Primary 6 carries three bands, which is why P6 is
                a group rather than one option. The labels are deliberately not Foundation or
                Standard — those name real MOE streams and a parent would read them as a claim
                about the national syllabus, which these bands are not.

                Starts at P6 Base, the easiest band (see FIRST_CHILD_DEFAULT_DIFFICULTY_TIER).
              */}
              <div className="space-y-1">
                <Label
                  htmlFor="first-child-difficulty"
                  className="text-sm font-medium text-gray-700"
                >
                  Learning level
                </Label>
                <select
                  id="first-child-difficulty"
                  value={difficultyTier}
                  onChange={(e) => setDifficultyTier(e.target.value as DifficultyTier)}
                  disabled={isPending}
                  className="h-11 w-full rounded-md border border-input bg-white px-3 text-sm text-gray-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-tutor-400"
                >
                  {GRADE_LEVEL_OPTIONS.map(({ label, value }) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                  <optgroup label="Primary 6">
                    {P6_BAND_OPTIONS.map(({ label, value }) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                  </optgroup>
                </select>
                <p className="text-xs text-gray-400">
                  You can change this anytime from your child&apos;s card.
                </p>
              </div>

              {/* Inline server error — TO-LL-036: surfaced, never swallowed */}
              {serverErrorMessage && (
                <div
                  role="alert"
                  className="rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700"
                >
                  {serverErrorMessage}
                </div>
              )}

              {/* Action buttons */}
              <div className="flex gap-3 pt-2">
                <Button
                  type="submit"
                  disabled={isPending}
                  className="flex-1 h-11"
                >
                  {isPending ? 'Creating…' : 'Create & start trial'}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  disabled={isPending}
                  onClick={handleClose}
                  className="flex-1 h-11"
                >
                  Cancel
                </Button>
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
